<template>
    <!-- SSH 文件管理：目录浏览 / 下载 / 上传 -->
    <el-drawer v-model="drawerVisible" class="ssh-file-drawer" direction="btt" :show-close="false" size="80%">
        <template #header="{ close, titleId, titleClass }">
            <div :id="titleId" :class="[titleClass, 'ssh-file-head']">
                <span class="ssh-file-head__machine">{{ machine.name }}</span>
                <span class="ssh-file-head__addr">{{ machine.address }}</span>
                <span class="ssh-file-head__count">{{ items.length }} 项</span>
            </div>
            <div class="ssh-file-head__ops">
                <el-button class="ssh-file-btn" size="small" :disabled="loading || uploading" @click="refresh">
                    <el-icon class="ssh-file-btn__icon">
                        <Refresh />
                    </el-icon>
                    刷新
                </el-button>
                <el-button class="ssh-file-btn" size="small" type="primary" plain :loading="uploading"
                    :disabled="loading" @click="pickFile">
                    <el-icon v-if="!uploading" class="ssh-file-btn__icon">
                        <Upload />
                    </el-icon>
                    上传到当前目录
                </el-button>
                <el-button class="ssh-file-btn" size="small" type="danger" plain @click="close">关闭</el-button>
            </div>
        </template>

        <div class="ssh-file-body">
            <!-- 路径栏 -->
            <div class="ssh-file-path">
                <el-button class="ssh-file-up" size="small" text :disabled="!canGoUp || loading" @click="goUp">
                    <el-icon class="ssh-file-up__icon">
                        <ArrowLeft />
                    </el-icon>
                    上级
                </el-button>
                <span class="ssh-file-path__cur" :title="path || '登录用户 home 目录'">
                    {{ path || '（登录用户 home 目录）' }}
                </span>
                <el-checkbox v-model="showHidden" class="ssh-file-hidden" label="显示隐藏文件" size="small" />
            </div>

            <!-- 文件列表 -->
            <div class="ssh-file-table-wrap">
                <el-table v-loading="loading" :data="visibleItems" height="100%" size="small" class="ssh-file-table"
                    @row-dblclick="onRowDblclick">
                    <el-table-column label="名称" min-width="320" show-overflow-tooltip>
                        <template #default="{ row }">
                            <span class="ssh-file-name" :class="{ 'is-dir': row.dir }" @click="onNameClick(row)">
                                <el-icon class="ssh-file-name__icon">
                                    <Folder v-if="row.dir" />
                                    <Link v-else-if="row.link" />
                                    <Document v-else />
                                </el-icon>
                                <span class="ssh-file-name__text">{{ row.name }}</span>
                                <span v-if="row.link" class="ssh-file-name__link">软链接</span>
                            </span>
                        </template>
                    </el-table-column>
                    <el-table-column label="大小" width="110" align="right">
                        <template #default="{ row }">{{ row.dir ? '-' : row.sizeText }}</template>
                    </el-table-column>
                    <el-table-column prop="modifyTime" label="修改时间" width="175" />
                    <el-table-column prop="permission" label="权限" width="115" />
                    <el-table-column label="操作" width="130" align="center">
                        <template #default="{ row }">
                            <template v-if="!row.dir">
                                <el-button v-if="canPreview(row)" class="ssh-file-op" size="small" text type="primary"
                                    @click.stop="openPreview(row)">
                                    预览
                                </el-button>
                                <el-button class="ssh-file-op" size="small" text type="primary"
                                    :disabled="downloading" @click.stop="download(row)">
                                    下载
                                </el-button>
                            </template>
                        </template>
                    </el-table-column>
                    <template #empty>
                        <span class="ssh-file-empty">{{ loading ? '加载中…' : '这个目录是空的' }}</span>
                    </template>
                </el-table>
            </div>
        </div>

        <!-- 隐藏的上传输入框（用原生 input，避免额外引入组件） -->
        <input ref="fileInput" class="ssh-file-input" type="file" @change="onFilePicked" />
    </el-drawer>

    <!-- 文本预览：不方便下载的场景可以直接看内容 -->
    <el-dialog v-model="preview.visible" class="ssh-file-preview" width="80%" top="6vh" append-to-body
        :title="preview.name || '文件预览'">
        <div class="ssh-file-preview__bar">
            <span class="ssh-file-preview__path" :title="preview.path">{{ preview.path }}</span>
            <span class="ssh-file-preview__size">{{ preview.sizeText }}</span>
            <el-button class="ssh-file-preview__btn" size="small" type="primary" plain
                :disabled="preview.loading || !preview.rawContent" @click="toggleJsonFormat">
                {{ preview.formatted ? '还原原文' : '格式化 JSON' }}
            </el-button>
        </div>
        <div v-loading="preview.loading" class="ssh-file-preview__body">
            <div class="ssh-file-preview__inner">
                <pre class="ssh-file-preview__gutter">{{ previewLineNumbers }}</pre>
                <pre class="ssh-file-preview__code">{{ previewText }}</pre>
            </div>
        </div>
    </el-dialog>
</template>

<script>
import { ElMessage, ElMessageBox } from 'element-plus';
import request from '@/utils/request.js';
import { config } from '@/utils/config';

export default {
    name: 'SshFileDrawer',
    props: {
        visible: { type: Boolean, default: false },
        machine: { type: Object, default: () => ({}) }
    },
    emits: ['update:visible'],
    data() {
        return {
            // 当前目录（由后端返回的规范化绝对路径）
            path: '',
            items: [],
            loading: false,
            // 同一时刻只允许一个下载/上传，避免误触
            downloading: false,
            uploading: false,
            showHidden: false,
            // 预览大小上限（由后端 list 接口下发，取值见 SshSftpService.PREVIEW_MAX_BYTES）
            previewMaxBytes: 0,
            preview: {
                visible: false,
                loading: false,
                name: '',
                path: '',
                sizeText: '',
                content: '',
                // 原文永久保留：JSON 格式化以它为源，反复切换不会层层缩进
                rawContent: '',
                formatted: false
            }
        };
    },
    computed: {
        drawerVisible: {
            get() {
                return this.visible;
            },
            set(value) {
                this.$emit('update:visible', value);
            }
        },
        visibleItems() {
            // 后端会返回点开头的隐藏文件，这里做一个显示开关
            return this.showHidden ? this.items : this.items.filter(item => !item.name.startsWith('.'));
        },
        canGoUp() {
            return !!this.path && this.path !== '/';
        },
        /**
         * 去掉结尾的一个换行：
         * `pre` 对"结尾空行"的渲染在各浏览器上不一致，而这里行号与内容必须严格 1:1，
         * 所以两者都基于这个字符串计算，避免底部多出一个行号
         */
        previewText() {
            const content = this.preview.content || '';
            return content.endsWith('\n') ? content.slice(0, -1) : content;
        },
        /**
         * 行号列的内容。
         * 【为什么用整块文本而不是逐行 v-for】逐行渲染会产生几万个 DOM 节点，大文件直接把页面卡死；
         * 这里整列只是一个文本节点，靠相同的 font-size / line-height 与内容严格对齐。
         */
        previewLineNumbers() {
            const text = this.previewText;
            if (!text) {
                return '';
            }
            const count = text.split('\n').length;
            const lines = new Array(count);
            for (let i = 0; i < count; i++) {
                lines[i] = i + 1;
            }
            return lines.join('\n');
        }
    },
    watch: {
        visible(value) {
            if (value) {
                this.$nextTick(() => this.load(''));
            } else {
                this.reset();
            }
        }
    },
    methods: {
        reset() {
            this.path = '';
            this.items = [];
            this.loading = false;
            this.downloading = false;
            this.uploading = false;
        },

        // ---------- 浏览 ----------
        refresh() {
            this.load(this.path);
        },
        goUp() {
            if (!this.canGoUp) {
                return;
            }
            const index = this.path.lastIndexOf('/');
            this.load(index <= 0 ? '/' : this.path.substring(0, index));
        },
        onNameClick(row) {
            if (row.dir) {
                this.load(row.path);
            }
        },
        onRowDblclick(row) {
            if (row.dir) {
                this.load(row.path);
            }
        },
        load(path) {
            if (!this.machine || !this.machine.id) {
                return;
            }
            this.loading = true;
            // path 传空字符串时后端会打开登录用户的 home 目录
            request({
                url: '/serverManFile/list',
                method: 'post',
                data: { machineId: this.machine.id, path: path || '' }
            })
                .then(res => {
                    this.path = res.meta.path;
                    this.items = res.meta.items || [];
                    this.previewMaxBytes = res.meta.previewMaxBytes || 0;
                })
                .catch(() => {
                    // 错误提示由 request 拦截器统一处理
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        // ---------- 下载 ----------
        download(row) {
            if (this.downloading) {
                return;
            }
            this.downloading = true;
            // 浏览器原生下载无法自定义请求头，所以先用正常鉴权换一张一次性票
            request({
                url: '/serverManFile/ticket',
                method: 'post',
                data: { machineId: this.machine.id, path: row.path }
            })
                .then(res => {
                    this.openDownloadUrl(res.meta.ticket);
                })
                .catch(() => {})
                .finally(() => {
                    this.downloading = false;
                });
        },
        openDownloadUrl(ticket) {
            // 后端返回的是 Content-Disposition: attachment，浏览器会在新标签里开始下载并自动关掉它；
            // 用新标签而不是当前页跳转，是为了让票失效等错误不影响当前页面
            const baseServer = (config() && config().baseServer) || '';
            const url = baseServer.replace(/\/+$/, '') + '/openapi/serverManFile/download?ticket='
                + encodeURIComponent(ticket);
            const link = document.createElement('a');
            link.href = url;
            link.target = '_blank';
            link.rel = 'noopener';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },

        // ---------- 预览 ----------
        canPreview(row) {
            // 后端还会再校验一次，这里只是避免给用户一个必然失败的按钮
            return !row.dir && (!this.previewMaxBytes || row.size <= this.previewMaxBytes);
        },
        openPreview(row) {
            this.preview.visible = true;
            this.preview.loading = true;
            this.preview.name = row.name;
            this.preview.path = row.path;
            this.preview.sizeText = row.sizeText;
            this.preview.content = '';
            this.preview.rawContent = '';
            this.preview.formatted = false;

            request({
                url: '/serverManFile/preview',
                method: 'post',
                data: { machineId: this.machine.id, path: row.path }
            })
                .then(res => {
                    this.preview.name = res.meta.name;
                    this.preview.path = res.meta.path;
                    this.preview.rawContent = res.meta.content || '';
                    this.preview.content = this.preview.rawContent;
                })
                .catch(() => {
                    // 失败原因（过大 / 二进制 / 无权限）已由拦截器提示，这里直接关掉弹窗
                    this.preview.visible = false;
                })
                .finally(() => {
                    this.preview.loading = false;
                });
        },
        toggleJsonFormat() {
            if (this.preview.formatted) {
                this.preview.content = this.preview.rawContent;
                this.preview.formatted = false;
                return;
            }
            try {
                // 始终以原文为源做格式化，来回切换不会层层缩进
                const parsed = JSON.parse(this.preview.rawContent);
                this.preview.content = JSON.stringify(parsed, null, 4);
                this.preview.formatted = true;
            } catch (e) {
                ElMessage({ message: '内容不是合法的 JSON，无法格式化', type: 'warning', duration: 3 * 1000 });
            }
        },

        // ---------- 上传 ----------
        pickFile() {
            // 先清空，否则连续选同一个文件不会触发 change
            this.$refs.fileInput.value = '';
            this.$refs.fileInput.click();
        },
        onFilePicked(event) {
            const file = event.target.files && event.target.files[0];
            if (!file) {
                return;
            }
            // 当前目录已有同名文件时先确认一次；后端也会挡，这里只是给出更友好的交互
            const conflict = this.items.some(item => item.name === file.name);
            if (conflict) {
                ElMessageBox.confirm(`当前目录已存在同名文件「${file.name}」，确定要覆盖吗？`, '覆盖确认', {
                    confirmButtonText: '覆盖',
                    cancelButtonText: '取消',
                    type: 'warning'
                })
                    .then(() => this.upload(file, true))
                    .catch(() => {});
            } else {
                this.upload(file, false);
            }
        },
        upload(file, overwrite) {
            const form = new FormData();
            form.append('machineId', this.machine.id);
            form.append('path', this.path || '');
            form.append('overwrite', overwrite ? 'true' : 'false');
            form.append('file', file);

            this.uploading = true;
            request({
                url: '/serverManFile/upload',
                method: 'post',
                data: form,
                // 大文件上传可能远超默认的 120 秒，这里显式关闭超时
                timeout: 0
            })
                .then(res => {
                    ElMessage.success('上传完成：' + res.meta.path);
                    this.refresh();
                })
                .catch(() => {})
                .finally(() => {
                    this.uploading = false;
                });
        }
    }
};
</script>

<style lang="scss" scoped>
.ssh-file-drawer {
    overflow: hidden;
    border-radius: 16px 16px 0 0;
}

.ssh-file-drawer .el-drawer__header {
    gap: 12px;
    margin-bottom: 0;
    padding: 10px 16px;
    border-bottom: 1px solid #eef1f6;
}

.ssh-file-drawer .el-drawer__title {
    margin: 0;
}

/* 抽屉主体用纵向 flex，让表格自动撑满剩余高度 */
.ssh-file-drawer .el-drawer__body {
    display: flex;
    flex-direction: column;
    padding: 0;
    overflow: hidden;
}

.ssh-file-head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    min-width: 0;
}

.ssh-file-head__machine {
    font-size: 15px;
    font-weight: 600;
    color: #1f2d3d;
}

.ssh-file-head__addr {
    font-size: 13px;
    color: #8a94a6;
}

.ssh-file-head__count {
    padding: 2px 10px;
    border-radius: 999px;
    background-color: var(--el-color-primary-light-9);
    color: var(--el-color-primary);
    font-size: 12px;
    font-weight: 500;
}

.ssh-file-head__ops {
    display: flex;
    flex: none;
    align-items: center;
    gap: 8px;
}

.ssh-file-btn {
    margin-left: 0;
    border-radius: 9px;
}

.ssh-file-btn__icon {
    margin-right: 4px;
}

/* ---------- 主体 ---------- */
.ssh-file-body {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
}

.ssh-file-path {
    display: flex;
    flex: none;
    align-items: center;
    gap: 10px;
    padding: 8px 16px;
    border-bottom: 1px solid #f2f5fa;
    background-color: #fbfcfe;
}

.ssh-file-up {
    flex: none;
    margin-left: 0;
    padding: 0 6px;
}

.ssh-file-up__icon {
    margin-right: 2px;
}

.ssh-file-path__cur {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 13px;
    color: #1f2d3d;
}

.ssh-file-hidden {
    flex: none;
    margin-right: 0;
}

.ssh-file-table-wrap {
    flex: 1 1 auto;
    min-height: 0;
}

.ssh-file-table .el-table__cell {
    padding: 4px 0;
}

/* 名称列：目录可点击进入 */
.ssh-file-name {
    display: inline-flex;
    align-items: center;
    min-width: 0;
    font-size: 13px;
    color: #4a5568;
}

.ssh-file-name.is-dir {
    color: var(--el-color-primary);
    cursor: pointer;
}

.ssh-file-name.is-dir:hover .ssh-file-name__text {
    text-decoration: underline;
}

.ssh-file-name__icon {
    flex: none;
    margin-right: 6px;
    color: #a3b1c6;
}

.ssh-file-name.is-dir .ssh-file-name__icon {
    color: var(--el-color-primary-light-3);
}

.ssh-file-name__text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.ssh-file-name__link {
    flex: none;
    margin-left: 6px;
    padding: 0 6px;
    border-radius: 999px;
    background-color: #f4f4f5;
    color: #909399;
    font-size: 11px;
}

.ssh-file-op {
    padding: 0 4px;
}

.ssh-file-empty {
    font-size: 13px;
    color: #aab4c4;
}

/* ---------- 预览弹窗 ---------- */
.ssh-file-preview__bar {
    display: flex;
    align-items: center;
    gap: 10px;
    padding-bottom: 10px;
}

.ssh-file-preview__path {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 12px;
    color: #8a94a6;
}

.ssh-file-preview__size {
    flex: none;
    font-size: 12px;
    color: #8a94a6;
}

.ssh-file-preview__btn {
    flex: none;
    margin-left: 0;
    border-radius: 8px;
}

.ssh-file-preview__body {
    max-height: 64vh;
    min-height: 120px;
    overflow: auto;
    border: 1px solid #eef1f6;
    border-radius: 10px;
    background-color: #fbfcfe;
}

.ssh-file-preview__inner {
    display: flex;
    align-items: flex-start;
    width: max-content;
    min-width: 100%;
}

/* 行号列：sticky 保证横向滚动时也一直可见 */
.ssh-file-preview__gutter {
    position: sticky;
    left: 0;
    z-index: 1;
    flex: none;
    margin: 0;
    padding: 12px 10px 12px 14px;
    border-right: 1px solid #eef1f6;
    background-color: #f6f8fb;
    text-align: right;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 12.5px;
    line-height: 1.6;
    color: #a3b1c6;
    user-select: none;
}

/* 内容列：不换行，才能与行号严格 1:1 对齐（长行靠横向滚动查看） */
.ssh-file-preview__code {
    flex: none;
    margin: 0;
    padding: 12px 14px;
    white-space: pre;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 12.5px;
    line-height: 1.6;
    color: #1f2d3d;
    tab-size: 4;
}

/* 隐藏的文件选择框 */
.ssh-file-input {
    display: none;
}

/* ---------- 移动端适配 ---------- */
@media (max-width: 768px) {
    .ssh-file-drawer .el-drawer__header {
        padding: 8px 12px;
    }

    .ssh-file-path {
        padding: 8px 12px;
    }

    .ssh-file-head__ops {
        flex-wrap: wrap;
    }
}
</style>

<!-- 预览弹窗：append-to-body 之后拿不到 scoped 属性，Element 内部结构的样式要放在非 scoped 块里 -->
<style lang="scss">
.ssh-file-preview .el-dialog__body {
    padding: 0 16px 16px;
}
</style>
